import { useCallback, useState } from "react";
import { toast } from "sonner";
import { supabaseClient } from "@/config/supabaseClient";
import { subscriptionApi } from "../api";
import { useSubscription } from "./useSubscriptionQuery";
import { openPaddleCheckout } from "../services/paddleClient";
import { redirectToStripeCheckout } from "../services/stripeClient";
import type { CicloFacturacion, Plan } from "../schemas";
import { track } from "@/utils/analytics";
import { rememberCheckout } from "../utils/checkoutAttribution";

const getStripePriceId = (plan: Plan, cycle: CicloFacturacion): string | null => {
	const id = cycle === "anual" ? plan.stripe_plan_id_anual : plan.stripe_plan_id_mensual;
	return typeof id === "string" && id.trim() ? id : null;
};

const getPaddlePriceId = (plan: Plan, cycle: CicloFacturacion): string | null => {
	const id = cycle === "anual" ? plan.proveedor_plan_id_anual : plan.proveedor_plan_id_mensual;
	return typeof id === "string" && id.trim() ? id : null;
};

const getCheckoutErrorMessage = (error: unknown): string => {
	if (!(error instanceof Error)) {
		return "Error al procesar el pago. Inténtalo de nuevo.";
	}

	if (error.message.includes("VITE_PADDLE_CLIENT_TOKEN")) {
		return "Falta configurar VITE_PADDLE_CLIENT_TOKEN en el frontend.";
	}
	if (error.message.includes("Paddle.js no disponible")) {
		return "Paddle.js no está disponible en el navegador.";
	}
	if (error.message.includes("No se pudo cargar Paddle.js")) {
		return "No se pudo cargar Paddle.js. Revisa bloqueadores/extensiones o red.";
	}
	return error.message || "Error al procesar el pago. Inténtalo de nuevo.";
};

/**
 * Sends the user to pay for a plan: the Stripe portal when they already hold a
 * Stripe subscription, else a new Stripe checkout, else Paddle. Shared by the
 * pricing page and every upsell that offers a direct upgrade.
 */
export function usePlanCheckout() {
	const [processingPlanId, setProcessingPlanId] = useState<number | null>(null);
	const { data: subscriptionResponse } = useSubscription();
	const subscription = subscriptionResponse?.suscripcion;

	const startCheckout = useCallback(
		async (plan: Plan, billingCycle: CicloFacturacion, source = "unknown") => {
			if (processingPlanId !== null) return;

			const trackStart = (provider: string) => {
				const ctx = {
					plan_id: plan.id,
					plan_name: plan.nombre,
					billing_cycle: billingCycle,
					provider,
					source,
				};
				track("checkout_started", ctx);
				rememberCheckout(ctx);
			};

			try {
				setProcessingPlanId(plan.id);

				// If user already has an active Stripe subscription, use the portal update flow
				const activeProveedor = subscription?.proveedor?.toLowerCase();
				if (activeProveedor === "stripe" && subscription?.proveedor_pago_id) {
					const result = await subscriptionApi.getPortalUrl("stripe", "update");
					if (result.url) {
						trackStart("stripe_portal");
						window.location.href = result.url;
						return;
					}
				}

				// New subscriber: Stripe checkout is primary; fall back to Paddle
				const stripeId = getStripePriceId(plan, billingCycle);
				if (stripeId) {
					trackStart("stripe");
					await redirectToStripeCheckout({ planId: plan.id, ciclo: billingCycle });
					return;
				}

				const paddleId = getPaddlePriceId(plan, billingCycle);
				if (paddleId) {
					const { data: { session } } = await supabaseClient.auth.getSession();
					const userId = session?.user?.id;
					if (!userId) {
						toast.error("No hay sesión activa. Inicia sesión nuevamente.");
						return;
					}
					trackStart("paddle");
					await openPaddleCheckout({ priceId: paddleId, userId, email: session?.user?.email, locale: "es" });
					return;
				}

				toast.error(`El plan ${plan.nombre} no tiene un método de pago configurado para ciclo ${billingCycle}.`);
			} catch (error) {
				console.error("Error opening checkout", error);
				toast.error(getCheckoutErrorMessage(error));
			} finally {
				setProcessingPlanId(null);
			}
		},
		[processingPlanId, subscription],
	);

	return { startCheckout, processingPlanId };
}
